import React, { useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Page,
  Text,
  BlockStack,
  Badge,
  Button,
  Banner,
  Modal,
} from "@shopify/polaris";
import { CheckCircleIcon } from "@shopify/polaris-icons";
import { Icon } from "@shopify/polaris";
import { useAppTitleBar } from "../hooks/useAppTitleBar";
import { useBilling } from "../context/BillingContext";
import PageLoader from "../components/PageLoader";

function PlanFeature({ label }) {
  return (
    <div className="age-plans-feature">
      <span className="age-plans-feature__icon" aria-hidden>
        <Icon source={CheckCircleIcon} tone="success" />
      </span>
      <span className="age-plans-feature__label">
        <Text as="span" variant="bodyMd">
          {label}
        </Text>
      </span>
    </div>
  );
}

function priceLabel(plan) {
  const amount = Number(plan.price);
  if (!Number.isFinite(amount) || amount <= 0) return "$0";
  return `$${amount.toFixed(2)}`;
}

export default function PlansPage() {
  useAppTitleBar({ title: "Plans & Billing" });
  const location = useLocation();
  const shopInfo = useSelector((state) => state.install.installData?.shop_info);
  const installChecked = useSelector((state) => state.install.checked);
  const billing = useBilling();
  const [busyPlan, setBusyPlan] = useState("");
  const [error, setError] = useState("");
  const [confirmFree, setConfirmFree] = useState(false);

  const billingNotice = useMemo(() => {
    const status = new URLSearchParams(location.search).get("billing");
    if (status === "success") {
      return { tone: "success", message: "Pro is active. Unlimited events and every calendar feature are available." };
    }
    if (status === "declined") {
      return { tone: "warning", message: "The charge was not approved in Shopify. This store remains on the Free plan." };
    }
    if (status === "error") {
      return { tone: "critical", message: "We could not confirm your subscription. Refresh this page and try again." };
    }
    return null;
  }, [location.search]);

  const showPlans = String(shopInfo?.user_type) === "0" && !billing.isLegacy;
  if (installChecked && billing.ready && !showPlans) {
    return <Navigate to={{ pathname: "/", search: location.search }} replace />;
  }

  const onChoose = async (planCode) => {
    setError("");
    if (planCode === "free" && billing.planCode === "pro") {
      setConfirmFree(true);
      return;
    }
    setBusyPlan(planCode);
    try {
      await billing.selectPlan(planCode);
    } catch (err) {
      setError(err?.message || "Could not change plan");
    } finally {
      setBusyPlan("");
    }
  };

  const confirmDowngrade = async () => {
    setError("");
    setBusyPlan("free");
    try {
      await billing.selectPlan("free");
      setConfirmFree(false);
    } catch (err) {
      setError(err?.message || "Could not downgrade to Free");
    } finally {
      setBusyPlan("");
    }
  };

  return (
    <Page>
      {billing.loading && !billing.ready ? <PageLoader /> : null}
      <BlockStack gap="500">
        {billingNotice ? <Banner tone={billingNotice.tone}>{billingNotice.message}</Banner> : null}
        {error || billing.error ? (
          <Banner tone="critical">{error || billing.error}</Banner>
        ) : null}
        {billing.pendingConfirmationUrl && billing.planCode !== "pro" ? (
          <Banner
            tone="info"
            action={{
              content: "Continue in Shopify",
              onAction: () => onChoose("pro"),
            }}
          >
            Pro is waiting for approval. You will not be billed until you confirm the charge in Shopify.
          </Banner>
        ) : null}

        <div className="age-plans-lead">
          <Text as="h2" variant="headingMd">
            Choose a plan
          </Text>
          <Text as="p" tone="subdued">
            Billed monthly through your Shopify account. You can change or cancel your plan at any time.
          </Text>
        </div>

        <div className="age-plans-grid">
          {(billing.plans.length ? billing.plans : FALLBACK_PLANS).map((plan) => {
            const copy = PLAN_COPY[plan.code] || {};
            const active = plan.active || plan.code === billing.planCode;
            const features = copy.features || plan.features;
            return (
              <div
                key={plan.code}
                className={`age-plans-card${plan.code === "pro" ? " age-plans-card--pro" : ""}${active ? " age-plans-card--active" : ""}`}
              >
                <div className="age-plans-card__body">
                  <div className="age-plans-card__header">
                    <Text as="h2" variant="headingLg">
                      {copy.name || plan.name}
                    </Text>
                    {active ? <Badge tone="success">Current plan</Badge> : null}
                    {!active && plan.code === "pro" ? <Badge tone="info">Recommended</Badge> : null}
                  </div>
                  <p className="age-plans-card__summary">{copy.summary}</p>
                  <p className="age-plans-pricing__main">
                    <span className="age-plans-pricing__amount">{priceLabel(plan)}</span>
                    <span className="age-plans-pricing__period"> / month</span>
                  </p>
                  <div className="age-plans-card__features">
                    {features.map((feature) => (
                      <PlanFeature key={feature} label={feature} />
                    ))}
                  </div>
                  <div className="age-plans-card__cta">
                    <Button
                      variant={active ? "secondary" : "primary"}
                      disabled={!billing.ready || active || Boolean(busyPlan)}
                      loading={busyPlan === plan.code}
                      onClick={() => onChoose(plan.code)}
                      fullWidth
                    >
                      {active ? "Current plan" : plan.code === "pro" ? "Upgrade to Pro" : "Downgrade to Free"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="age-plans-note">
          Charges appear on your Shopify bill. Downgrading does not delete events already saved in your admin.
        </p>
      </BlockStack>

      <Modal
        open={confirmFree}
        onClose={() => (busyPlan ? null : setConfirmFree(false))}
        title="Downgrade to Free?"
        primaryAction={{
          content: "Downgrade to Free",
          destructive: true,
          loading: busyPlan === "free",
          onAction: confirmDowngrade,
        }}
        secondaryActions={[
          { content: "Keep Pro", disabled: Boolean(busyPlan), onAction: () => setConfirmFree(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Your $2.99 subscription will be cancelled in Shopify. Events already created stay in your admin.
            </Text>
            <Text as="p" variant="bodyMd">
              On Free, your storefront publishes one event on the Classic month template. Registration forms are turned off.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

const PLAN_COPY = {
  free: {
    name: "Free",
    summary: "Publish a single event while you get started.",
    features: [
      "One published event",
      "Classic month template",
      "Registration forms not included",
    ],
  },
  pro: {
    name: "Pro",
    summary: "Run events regularly, with every template and registration.",
    features: [
      "Unlimited published events",
      "Every calendar template",
      "Registration forms",
      "Signup alerts and email notifications",
      "Full widget customization",
    ],
  },
};

const FALLBACK_PLANS = [
  { code: "free", name: "Free", price: "0.00", features: PLAN_COPY.free.features },
  { code: "pro", name: "Pro", price: "2.99", features: PLAN_COPY.pro.features },
];
